import { useState } from 'react'

import { currentPageParams, DataClass, DataItem, getDataClass } from 'scrivito'

import { useOnEscPress } from '@/hooks/useOnEscPress'
import { writeUrlParams } from '@/hooks/useUrlParams'
import { getDataItem } from '@/utils/getDataItem'
import { isString } from '@/utils/isString'

/**
 * Generic useDrawer hook
 */
export const useDrawer = <T>() => {
  const [drawerItem, setDrawerItem] = useState<T>()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const openDrawer = () => setIsDrawerOpen(true)
  const closeDrawer = () => setIsDrawerOpen(false)

  useOnEscPress({ onEscPress: closeDrawer })

  return {
    drawerItem,
    setDrawerItem,
    isDrawerOpen,
    setIsDrawerOpen,
    openDrawer,
    closeDrawer,
  }
}

export const useDataItemDrawer = () => {
  const [drawerItem, setItem] = useState<DataItem>()
  const [startInEditMode, setStartInEditMode] = useState(false)
  // Visibility is URL-derived (see activeDrawerOpen); openDrawer only carries
  // the startInEditMode intent — setDrawerItem already wrote the URL params.
  const openDrawer = (startInEditMode: boolean = false) => {
    setStartInEditMode(startInEditMode)
  }

  const { drawer, drawer_id } = currentPageParams()
  const closeDrawer = () => {
    writeUrlParams({ drawer: null, drawer_id: null })
    setStartInEditMode(false)
  }

  const setDrawerItem = (dataItem: DataItem) => {
    writeUrlParams({
      drawer: dataItem.dataClassName(),
      drawer_id: dataItem.id(),
    })

    setItem(dataItem)
  }

  useOnEscPress({ onEscPress: closeDrawer })

  let currentDataItem: DataItem | null = null
  let currentDataClass: DataClass | null = null
  if (isString(drawer)) {
    currentDataClass = getDataClass(drawer)
  }

  const isCreateDrawer = drawer_id === `new_${drawer}`

  if (isString(drawer_id) && currentDataClass && !isCreateDrawer) {
    currentDataItem = getDataItem(currentDataClass, drawer_id).dataItem
  }

  const activeDrawerItem = currentDataItem ?? drawerItem
  // Open state is derived purely from the URL: every opener calls setDrawerItem
  // (which writes the drawer/drawer_id params) before openDrawer, so the params
  // are the single source of truth. This keeps the browser back button working —
  // popping the params off the URL closes the drawer without relying on the
  // local isDrawerOpen flag, which is never reset on a back navigation.
  const activeDrawerOpen = !!currentDataItem

  /**
   * Util function to navigate between drawers of the same DataClass
   * @param id
   */
  const openDrawerId = (id?: string) => {
    if (!id) return
    writeUrlParams({ drawer_id: id })
  }

  return {
    drawerItem: activeDrawerItem,
    setDrawerItem,
    isDrawerOpen: activeDrawerOpen,
    openDrawer,
    closeDrawer,
    openDrawerId,
    startInEditMode,
    setStartInEditMode,
  }
}
